import Text from "@commons/Typography/Text";
import { FormHelperText } from "@mui/material";
import { TEXT_SIZES } from "@typings/components/text";
import { twMerge } from "tailwind-merge";

type BaseProps = {
  label?: string;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  textSize?: keyof typeof TEXT_SIZES;
  className?: string;
};

type InputProps = BaseProps & {
  multiline?: false;
} & React.InputHTMLAttributes<HTMLInputElement>;

type TextAreaProps = BaseProps & {
  multiline: true;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

type TextInputProps = InputProps | TextAreaProps;

function TextInput(props: TextInputProps) {
  const {
    label,
    error,
    helperText,
    fullWidth,
    textSize = "base",
    className,
    multiline,
    ...restProps
  } = props;

  const inputClasses = twMerge(
    "border rounded px-3 py-2 bg-white shadow-sm rounded-md font-roboto focus:outline-none focus:ring-1 min-h-10",
    TEXT_SIZES[textSize],
    error
      ? "border-red-500 focus:ring-red-500"
      : "border-gray-300 focus:ring-primary",
    fullWidth ? "w-full" : "",
    className,
  );

  return (
    <div className={`${fullWidth ? "w-full" : ""} min-w-32`}>
      {label && (
        <div className="mb-2 font-medium" data-testid="text-label">
          <Text>{label}</Text>
        </div>
      )}

      {multiline ? (
        <textarea
          {...(restProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          className={inputClasses}
          data-testid="text-input"
        />
      ) : (
        <input
          {...(restProps as React.InputHTMLAttributes<HTMLInputElement>)}
          className={inputClasses}
          data-testid="text-input"
        />
      )}

      <div>
        {helperText && (
          <FormHelperText error={!!error} className="ml-3">
            {helperText}
          </FormHelperText>
        )}
      </div>
    </div>
  );
}

export default TextInput;
